/**
 * Offline Storage Service using IndexedDB (ola 2 · O2.5: modo offline para turnos).
 *
 * Guarda el paquete de preguntas (mismo contrato público que /exam/start: sin
 * clave ni explicación), las flashcards vencidas, la cola de resultados y la
 * cola de repasos pendientes de sincronizar. La corrección de los tests la hace
 * el servidor al sincronizar (POST /offline/sync); aquí no hay nada que corregir.
 *
 * Las funciones exportadas sueltas (isCompleteOfflineQuestion, shuffleArray,
 * selectDueCards, formatBytes…) son puras y se prueban sin IndexedDB.
 */

const DB_NAME = 'TuPlazaIA_Offline';
const DB_VERSION = 3;
const QUESTIONS_STORE = 'questions';
const FLASHCARDS_STORE = 'flashcards';
const AUDIOS_STORE = 'audios';
const RESULTS_STORE = 'pending_results';
const REVIEWS_STORE = 'pending_reviews';
const META_STORE = 'metadata';

export const STORES = Object.freeze({
  questions: QUESTIONS_STORE,
  flashcards: FLASHCARDS_STORE,
  audios: AUDIOS_STORE,
  pending_results: RESULTS_STORE,
  pending_reviews: REVIEWS_STORE,
});

export const PACKAGE_META_KEY = 'package';
export const SYNCED_RESULTS_KEY = 'synced_results';
export const MAX_SYNCED_RESULTS = 20;
export const DEFAULT_REVIEW_SESSION = 25;

// ========== HELPERS PUROS ==========

// Claves que delatarían la respuesta: si el paquete las trajera, se descarta el
// ítem (defensa en profundidad; el servidor ya no las envía).
const ANSWER_MATERIAL_KEYS = ['correct_option', 'correct_option_id', 'correct_answer', 'explicacion',
  'explanation', 'outdated_note', 'protocol_vs_evidence'];

export const hasAnswerMaterial = (q) => {
  if (!q || typeof q !== 'object') return false;
  if (ANSWER_MATERIAL_KEYS.some((k) => q[k] !== undefined && q[k] !== null)) return true;
  return Array.isArray(q.options) && q.options.some((o) => o && typeof o === 'object' && o.is_correct !== undefined);
};

/** Pregunta utilizable sin conexión: id, enunciado y ≥ 2 opciones con option_id y texto. */
export const isCompleteOfflineQuestion = (q) => {
  if (!q || typeof q !== 'object') return false;
  if (!q.question_id) return false;
  const text = q.text || q.question_text;
  if (typeof text !== 'string' || !text.trim()) return false;
  if (!Array.isArray(q.options) || q.options.length < 2) return false;
  const ids = new Set();
  for (const o of q.options) {
    if (!o || typeof o !== 'object') return false;
    if (o.option_id === undefined || o.option_id === null) return false;
    if (typeof o.text !== 'string' || !o.text.trim()) return false;
    ids.add(String(o.option_id));
  }
  if (ids.size !== q.options.length) return false;
  return !hasAnswerMaterial(q);
};

/** Fisher–Yates sobre una copia (sort(() => Math.random() - 0.5) no es uniforme). */
export const shuffleArray = (arr, random = Math.random) => {
  const out = Array.isArray(arr) ? [...arr] : [];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

export const formatBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '0 KB';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

export const isPackageExpired = (meta, now = Date.now()) => {
  const exp = meta?.expires_at ? Date.parse(meta.expires_at) : NaN;
  return Number.isFinite(exp) && exp <= now;
};

/** Resumen persistido del paquete descargado (lo que enseña el menú offline). */
export const buildPackageMeta = (pkg, savedCount, skippedLocally = 0) => ({
  package_id: pkg?.package_id || null,
  version: pkg?.version || null,
  generated_at: pkg?.generated_at || pkg?.downloaded_at || new Date().toISOString(),
  expires_at: pkg?.expires_at || null,
  exam_config: pkg?.exam_config || null,
  questions_count: savedCount,
  skipped_count: (pkg?.skipped_count || 0) + skippedLocally,
  topics_count: pkg?.topics_count ?? (Array.isArray(pkg?.topics) ? pkg.topics.length : 0),
  approx_size_bytes: pkg?.approx_size_bytes || 0,
  correction: pkg?.correction || 'server',
});

// ---- Flashcards: cola de vencidas (espejo de flashcards.py; el servidor manda) ----

const STATE_PRIORITY = { learning: 0, relearning: 1, new: 2, review: 3 };

/** R = (1 + t / (9·S))^-1, la misma fórmula que fsrs_calculate_retrievability. */
export const cardRetrievability = (card, now = Date.now()) => {
  const s = Number(card?.stability) > 0 ? Number(card.stability) : 1;
  const last = card?.last_review ? Date.parse(card.last_review) : NaN;
  const elapsedDays = Number.isFinite(last) ? Math.max(0, (now - last) / 86400e3) : 0;
  return 1 / (1 + elapsedDays / (9 * s));
};

export const isCardDue = (card, now = Date.now()) => {
  if (!card) return false;
  if (card.state === 'learning' || card.state === 'relearning') return true;
  if (!card.next_review) return true;
  const next = Date.parse(card.next_review);
  return !Number.isFinite(next) || next <= now;
};

/**
 * Selección de la sesión de repaso offline: solo cartas valorables (con user_id o
 * reviewable !== false), vencidas primero ordenadas por estado → R ascendente →
 * next_review, con tope por tema para intercalar. Devuelve también total_due.
 */
export const selectDueCards = (cards, { limit = DEFAULT_REVIEW_SESSION, now = Date.now(), perTopicCap = 8 } = {}) => {
  const all = Array.isArray(cards) ? cards.filter(Boolean) : [];
  const reviewable = all.filter((c) => c.reviewable !== false);
  const due = reviewable
    .filter((c) => isCardDue(c, now))
    .map((c) => ({ card: c, r: cardRetrievability(c, now) }))
    .sort((a, b) => {
      const pa = STATE_PRIORITY[a.card.state] ?? 4;
      const pb = STATE_PRIORITY[b.card.state] ?? 4;
      if (pa !== pb) return pa - pb;
      if (a.r !== b.r) return a.r - b.r;
      return String(a.card.next_review || '').localeCompare(String(b.card.next_review || ''));
    });
  const perTopic = new Map();
  const picked = [];
  for (const { card } of due) {
    const topic = card.topic_id || '__sin_tema__';
    const n = perTopic.get(topic) || 0;
    if (perTopicCap > 0 && n >= perTopicCap) continue;
    perTopic.set(topic, n + 1);
    picked.push(card);
    if (picked.length >= limit) break;
  }
  if (picked.length < limit) {
    // Relleno con las vencidas que quedaron fuera por el tope de tema
    for (const { card } of due) {
      if (picked.length >= limit) break;
      if (!picked.includes(card)) picked.push(card);
    }
  }
  return {
    cards: picked,
    total_due: due.length,
    total_reviewable: reviewable.length,
    total: all.length,
  };
};

class OfflineStorageService {
  constructor() {
    this.db = null;
    this.isReady = false;
  }

  async init() {
    if (this.isReady && this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      // Otra pestaña con la versión anterior abierta bloquea la migración:
      // rechazar en vez de esperar para siempre (la página avisa y conserva en memoria).
      request.onblocked = () => reject(new Error('IndexedDB bloqueado por otra pestaña de TuPlazaIA'));

      request.onsuccess = () => {
        this.db = request.result;
        this.isReady = true;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Questions store
        if (!db.objectStoreNames.contains(QUESTIONS_STORE)) {
          const questionsStore = db.createObjectStore(QUESTIONS_STORE, { keyPath: 'question_id' });
          questionsStore.createIndex('topic_id', 'topic_id', { unique: false });
          questionsStore.createIndex('difficulty', 'difficulty', { unique: false });
        }

        // Flashcards store
        if (!db.objectStoreNames.contains(FLASHCARDS_STORE)) {
          const flashcardsStore = db.createObjectStore(FLASHCARDS_STORE, { keyPath: 'flashcard_id' });
          flashcardsStore.createIndex('topic_id', 'topic_id', { unique: false });
          flashcardsStore.createIndex('state', 'state', { unique: false });
        }

        // Audios store (metadata only)
        if (!db.objectStoreNames.contains(AUDIOS_STORE)) {
          const audiosStore = db.createObjectStore(AUDIOS_STORE, { keyPath: 'audio_id' });
          audiosStore.createIndex('topic_id', 'topic_id', { unique: false });
        }

        // Pending results store
        if (!db.objectStoreNames.contains(RESULTS_STORE)) {
          db.createObjectStore(RESULTS_STORE, { keyPath: 'offline_session_id' });
        }

        // Pending flashcard reviews (v3): valoraciones hechas sin conexión
        if (!db.objectStoreNames.contains(REVIEWS_STORE)) {
          const reviewsStore = db.createObjectStore(REVIEWS_STORE, { keyPath: 'review_id' });
          reviewsStore.createIndex('card_id', 'card_id', { unique: false });
          reviewsStore.createIndex('reviewed_at', 'reviewed_at', { unique: false });
        }

        // Metadata store
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE, { keyPath: 'key' });
        }
      };
    });
  }

  _request(storeName, mode, fn) {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(storeName, mode);
      const request = fn(tx.objectStore(storeName));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async _getAll(storeName) {
    await this.init();
    return this._request(storeName, 'readonly', (store) => store.getAll());
  }

  async _count(storeName) {
    await this.init();
    return this._request(storeName, 'readonly', (store) => store.count());
  }

  async _put(storeName, value) {
    await this.init();
    return this._request(storeName, 'readwrite', (store) => store.put(value));
  }

  async _get(storeName, key) {
    await this.init();
    return this._request(storeName, 'readonly', (store) => store.get(key));
  }

  async _deleteMany(storeName, keys) {
    await this.init();
    const tx = this.db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    for (const key of keys || []) {
      await new Promise((resolve) => {
        const request = store.delete(key);
        request.onsuccess = resolve;
        request.onerror = resolve;
      });
    }
  }

  /** Vacía un solo store (p. ej. 'questions' al redescargar) sin tocar las colas pendientes. */
  async clearStore(storeName) {
    const name = STORES[storeName] || storeName;
    if (![QUESTIONS_STORE, FLASHCARDS_STORE, AUDIOS_STORE, RESULTS_STORE, REVIEWS_STORE, META_STORE].includes(name)) {
      throw new Error(`Store desconocido: ${storeName}`);
    }
    await this.init();
    await this._request(name, 'readwrite', (store) => store.clear());
    return true;
  }

  // ========== QUESTIONS ==========

  async saveQuestions(questions) {
    if (!Array.isArray(questions) || questions.length === 0) return 0;
    await this.init();
    const tx = this.db.transaction(QUESTIONS_STORE, 'readwrite');
    const store = tx.objectStore(QUESTIONS_STORE);

    for (const question of questions) {
      await new Promise((resolve, reject) => {
        const request = store.put(question);
        request.onsuccess = resolve;
        request.onerror = () => reject(request.error);
      });
    }

    await this.setMeta('lastDownload', new Date().toISOString());
    await this.setMeta('questionCount', questions.length);

    return questions.length;
  }

  /**
   * Sustituye el paquete anterior por uno nuevo (GET /offline/package): descarta
   * ítems incompletos o con material de respuesta, borra las preguntas antiguas
   * (anuladas/retiradas desaparecen) y guarda los metadatos del paquete.
   */
  async savePackage(pkg) {
    const incoming = Array.isArray(pkg?.questions) ? pkg.questions : [];
    const valid = incoming.filter(isCompleteOfflineQuestion);
    const skippedLocally = incoming.length - valid.length;
    if (valid.length === 0) {
      return { saved: 0, skipped: skippedLocally, meta: null };
    }
    await this.clearStore(QUESTIONS_STORE);
    await this.saveQuestions(valid);
    const meta = buildPackageMeta(pkg, valid.length, skippedLocally);
    await this.setMeta(PACKAGE_META_KEY, meta);
    if (pkg?.exam_config) await this.setMeta('examConfig', pkg.exam_config);
    return { saved: valid.length, skipped: skippedLocally, meta };
  }

  async getPackageMeta() {
    return (await this.getMeta(PACKAGE_META_KEY)) || null;
  }

  async getExamConfig() {
    const meta = await this.getPackageMeta();
    return meta?.exam_config || (await this.getMeta('examConfig')) || null;
  }

  async getQuestions(limit = null) {
    const questions = await this._getAll(QUESTIONS_STORE);
    if (limit) {
      return shuffleArray(questions).slice(0, limit);
    }
    return questions;
  }

  /** Preguntas completas (contestables) barajadas, como mucho `limit`. */
  async getPlayableQuestions(limit = null) {
    const questions = (await this._getAll(QUESTIONS_STORE)).filter(isCompleteOfflineQuestion);
    const shuffled = shuffleArray(questions);
    return limit ? shuffled.slice(0, limit) : shuffled;
  }

  async getPlayableCount() {
    const questions = await this._getAll(QUESTIONS_STORE);
    return questions.filter(isCompleteOfflineQuestion).length;
  }

  /** question_id ya contestados con este paquete: colas pendientes y sesiones corregidas guardadas. */
  async getAnsweredQuestionIds() {
    const ids = new Set();
    const pending = await this.getPendingResults();
    for (const result of pending || []) {
      for (const a of result?.answers || []) if (a?.question_id) ids.add(a.question_id);
    }
    const synced = (await this.getMeta(SYNCED_RESULTS_KEY)) || {};
    for (const session of Object.values(synced)) {
      for (const row of session?.results || []) if (row?.question_id) ids.add(row.question_id);
    }
    return ids;
  }

  /**
   * Preguntas para un test nuevo: solo las NO contestadas todavía (una pregunta
   * cuya corrección ya está en el móvil no vuelve a salir), barajadas y acotadas.
   * Devuelve también cuántas quedan para que la página avise «Actualiza el paquete».
   */
  async getFreshQuestions(limit = null) {
    const playable = (await this._getAll(QUESTIONS_STORE)).filter(isCompleteOfflineQuestion);
    const answered = await this.getAnsweredQuestionIds();
    const fresh = shuffleArray(playable.filter((q) => !answered.has(q.question_id)));
    return {
      questions: limit ? fresh.slice(0, limit) : fresh,
      fresh: fresh.length,
      answered: playable.length - fresh.length,
      total: playable.length,
    };
  }

  async getQuestionsByTopic(topicId, limit = 10) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(QUESTIONS_STORE, 'readonly');
      const store = tx.objectStore(QUESTIONS_STORE);
      const index = store.index('topic_id');
      const request = index.getAll(topicId);

      request.onsuccess = () => {
        resolve(shuffleArray(request.result).slice(0, limit));
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ========== FLASHCARDS ==========

  async saveFlashcards(flashcards) {
    if (!Array.isArray(flashcards) || flashcards.length === 0) return 0;
    await this.init();
    const tx = this.db.transaction(FLASHCARDS_STORE, 'readwrite');
    const store = tx.objectStore(FLASHCARDS_STORE);

    for (const card of flashcards) {
      // Ensure flashcard has an ID
      const cardWithId = {
        ...card,
        flashcard_id: card.flashcard_id || card.card_id || `fc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
      await new Promise((resolve, reject) => {
        const request = store.put(cardWithId);
        request.onsuccess = resolve;
        request.onerror = () => reject(request.error);
      });
    }

    await this.setMeta('flashcardCount', flashcards.length);
    return flashcards.length;
  }

  async getFlashcards(limit = null) {
    const flashcards = await this._getAll(FLASHCARDS_STORE);
    if (limit) {
      return shuffleArray(flashcards).slice(0, limit);
    }
    return flashcards;
  }

  async getFlashcard(id) {
    return this._get(FLASHCARDS_STORE, id);
  }

  /** Actualización optimista local de una carta tras valorarla sin conexión. */
  async updateFlashcard(card) {
    if (!card) return false;
    const flashcard_id = card.flashcard_id || card.card_id;
    if (!flashcard_id) return false;
    await this._put(FLASHCARDS_STORE, { ...card, flashcard_id });
    return true;
  }

  /** Sesión de repaso: cartas vencidas ordenadas por urgencia (ver selectDueCards). */
  async getDueFlashcards(limit = DEFAULT_REVIEW_SESSION, now = Date.now()) {
    const cards = await this._getAll(FLASHCARDS_STORE);
    return selectDueCards(cards, { limit, now });
  }

  // ========== AUDIOS ==========

  async saveAudios(audios) {
    if (!Array.isArray(audios) || audios.length === 0) return 0;
    await this.init();
    const tx = this.db.transaction(AUDIOS_STORE, 'readwrite');
    const store = tx.objectStore(AUDIOS_STORE);

    for (const audio of audios) {
      const audioWithId = {
        ...audio,
        audio_id: audio.audio_id || audio.id || `audio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
      await new Promise((resolve, reject) => {
        const request = store.put(audioWithId);
        request.onsuccess = resolve;
        request.onerror = () => reject(request.error);
      });
    }

    await this.setMeta('audioCount', audios.length);
    return audios.length;
  }

  async getAudios() {
    return this._getAll(AUDIOS_STORE);
  }

  // ========== RESULTS ==========

  async savePendingResult(result) {
    await this._put(RESULTS_STORE, result);
    return true;
  }

  async getPendingResults() {
    return this._getAll(RESULTS_STORE);
  }

  async getPendingResult(id) {
    return this._get(RESULTS_STORE, id);
  }

  async clearPendingResults(sessionIds) {
    await this._deleteMany(RESULTS_STORE, sessionIds);
  }

  /** Corrección devuelta por el servidor para una sesión sincronizada (últimas N). */
  async saveSyncedResult(result) {
    const id = result?.session_local_id || result?.exam_session_id;
    if (!id) return false;
    const current = (await this.getMeta(SYNCED_RESULTS_KEY)) || {};
    const next = { ...current, [id]: result };
    const ids = Object.keys(next).sort((a, b) => String(next[b].synced_at || '').localeCompare(String(next[a].synced_at || '')));
    const trimmed = {};
    ids.slice(0, MAX_SYNCED_RESULTS).forEach((k) => { trimmed[k] = next[k]; });
    await this.setMeta(SYNCED_RESULTS_KEY, trimmed);
    return true;
  }

  async getSyncedResult(id) {
    const current = (await this.getMeta(SYNCED_RESULTS_KEY)) || {};
    return current[id] || null;
  }

  // ========== PENDING FLASHCARD REVIEWS ==========

  async savePendingReview(review) {
    if (!review?.review_id || !review?.card_id) return false;
    await this._put(REVIEWS_STORE, {
      origen: 'offline',
      reviewed_at: new Date().toISOString(),
      ...review,
    });
    return true;
  }

  async getPendingReviews() {
    const reviews = await this._getAll(REVIEWS_STORE);
    return reviews.sort((a, b) => String(a.reviewed_at || '').localeCompare(String(b.reviewed_at || '')));
  }

  async getPendingReviewCount() {
    return this._count(REVIEWS_STORE);
  }

  async clearPendingReviews(reviewIds) {
    await this._deleteMany(REVIEWS_STORE, reviewIds);
  }

  // ========== METADATA ==========

  async setMeta(key, value) {
    await this._put(META_STORE, { key, value });
    return true;
  }

  async getMeta(key) {
    const row = await this._get(META_STORE, key);
    return row?.value;
  }

  // ========== STATS ==========

  async getStats() {
    await this.init();

    const questionCount = await this.getQuestionCount();
    const playableCount = await this.getPlayableCount();
    const flashcardCount = await this.getFlashcardCount();
    const audioCount = await this.getAudioCount();
    const pendingResults = await this.getPendingResults();
    const pendingReviewsCount = await this.getPendingReviewCount();
    const lastDownload = await this.getMeta('lastDownload');
    const packageMeta = await this.getPackageMeta();

    return {
      questionCount,
      playableCount,
      flashcardCount,
      audioCount,
      pendingResultsCount: pendingResults.length,
      pendingReviewsCount,
      lastDownload: lastDownload || null,
      package: packageMeta,
      packageExpired: isPackageExpired(packageMeta),
      isAvailable: questionCount > 0 || flashcardCount > 0
    };
  }

  async getQuestionCount() {
    return this._count(QUESTIONS_STORE);
  }

  async getFlashcardCount() {
    return this._count(FLASHCARDS_STORE);
  }

  async getAudioCount() {
    return this._count(AUDIOS_STORE);
  }

  // ========== CLEAR ==========

  /** Borra el contenido descargado. Las colas pendientes (resultados y repasos)
   * solo se borran con keepPending=false: son trabajo de la alumna sin enviar. */
  async clearAll({ keepPending = false } = {}) {
    await this.init();
    const stores = keepPending
      ? [QUESTIONS_STORE, FLASHCARDS_STORE, AUDIOS_STORE, META_STORE]
      : [QUESTIONS_STORE, FLASHCARDS_STORE, AUDIOS_STORE, RESULTS_STORE, REVIEWS_STORE, META_STORE];
    const tx = this.db.transaction(stores, 'readwrite');

    await Promise.all(stores.map(storeName =>
      new Promise((resolve) => {
        tx.objectStore(storeName).clear().onsuccess = resolve;
      })
    ));

    return true;
  }

  // ========== TOPICS ==========

  async getAvailableTopics() {
    await this.init();
    const questions = await this.getQuestions();
    const topicsMap = new Map();

    for (const q of questions) {
      if (q.topic_id && !topicsMap.has(q.topic_id)) {
        topicsMap.set(q.topic_id, {
          topic_id: q.topic_id,
          name: q.topic_name || 'Tema desconocido',
          question_count: 0
        });
      }
      if (q.topic_id) {
        topicsMap.get(q.topic_id).question_count++;
      }
    }

    return Array.from(topicsMap.values());
  }
}

// Singleton instance
export const offlineStorage = new OfflineStorageService();
export default offlineStorage;
